import { useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import { isMockMode } from '@/api';
import { FLOW_ORDER } from '../stepMachine';
import { Button } from './Button';

/**
 * The resume-first overlay — the single biggest India-mobile funnel fix. Calm, never red.
 *
 * Two honest states, driven by the ConnectionState machine (UX_SPEC §3.6, PRD-SE-11/S7-8):
 *   • reconnecting — the transport is auto-recovering (ICE-restart): "Reconnecting… progress saved, stay on
 *     this screen". No action needed; a short blip is never a restart.
 *   • suspended — a longer drop: "Connection lost", with an explicit Rejoin that re-enters the SAME live room
 *     (/call/:sessionId re-mints a join token — the widened join guard now allows the reconnection band).
 *
 * It NEVER claims "reconnected": the store only moves to connected/resumed when the transport actually confirms,
 * never on optimism. Progress ("STEP n OF m KEPT · SAME VERIFIER") is the reassurance, not a false success.
 */
export function ReconnectingOverlay(): JSX.Element | null {
  const { t } = useT();
  const completed = useSessionStore((s) => s.session.completedSteps.length);
  const agentName = useSessionStore((s) => s.session.agentName);
  const connection = useSessionStore((s) => s.connection);
  const sessionId = useSessionStore((s) => s.session.sessionId);
  const suspended = connection === 'suspended';
  const total = FLOW_ORDER.length;
  const pct = Math.round((completed / total) * 100);

  // §5.4 — rejoin IN PLACE. Bumping the store's rejoin nonce re-runs the live-call connect effect: the
  // join token is re-minted and the SAME room re-entered, with no page navigation.
  //
  // This used to be `window.location.assign('/call/' + sessionId)`. That worked, and it re-mounted the
  // entire app — so consent, geo and the camera-permission prompt were re-run on EVERY reconnect. On a
  // 250 kbps link that is indistinguishable from "rejoin is broken": the customer is asked to re-grant
  // everything at the exact moment their connection is least able to carry it. Technically working and
  // practically unusable is still a defect.
  const rejoinNow = useSessionStore((s) => s.rejoinNow);
  const rejoin = () => {
    if (sessionId) rejoinNow();
  };

  // 2026-08-24 (owner: "Disconnect does not work on mobile"): this overlay used to render its markup
  // UNCONDITIONALLY and rely on the CSS gate `.call-stage[data-reconnect=true] .reconnect{display:flex}`
  // for its visibility — which meant `.reconnect` had to default to `display:none`. Four OTHER cards
  // share that class and are React-conditional (end-call confirm, you-left, session-ended,
  // verifier-lost): on a healthy call `data-reconnect` is "false", so every one of them rendered
  // INVISIBLY. Tapping End did fire, showed nothing, and read as a dead button. Visibility now belongs
  // to React here too, exactly like its four siblings, and the CSS gate is gone.
  if (connection !== 'reconnecting' && !suspended) return null;

  return (
    <div className="reconnect" role="status" aria-live="polite">
      <div className="rc-ring" />
      <div className="rc-title">{suspended ? t('droom.rc.lost') : t('droom.rc.reconnecting')}</div>
      <div className="rc-sub">
        {suspended ? t('droom.rc.sub.suspended') : t('droom.rc.sub.blip')}
      </div>
      <div className="rc-saved">
        <div className="bar">
          <i style={{ width: `${Math.max(pct, 12)}%` }} />
        </div>
        <div className="lb">
          {t(agentName ? 'droom.rc.kept.same' : 'droom.rc.kept', {
            step: String(completed),
            total: String(total),
          })}
        </div>
      </div>
      {suspended && !isMockMode() && sessionId ? (
        <Button variant="ghost" className="rc-rejoin" onClick={rejoin} type="button">
          {t('droom.rc.rejoin')}
        </Button>
      ) : null}
    </div>
  );
}
